import { addMinutes, compareAsc } from "date-fns";
import { v4 } from "uuid";
import { getTimeOffsetForMeal } from "./getTimeOffsetForMeal";
import { getMealModificationsForExternalMealMatches } from "./getMealModificationsForExternalMealMatches";
import { findMealModificationForMeal } from "./utils";
const isMealEvent = (event) => event.__typename === "meal";
const isActivityEvent = (event) => event.__typename === "activity";
const isSuggestedMealTemplate = (event) => event.type === "SuggestedMealTemplate";
export const getSuggestedMealEventsForMealPlanEvents = ({ mealPlanEvents, externalEvents, modifiedMealTemplateIds, }) => {
    const { meals } = getSuggestedNewMealSchedule({ mealPlanEvents, externalEvents, modifiedMealTemplateIds });
    return meals.filter(isSuggestedMealTemplate);
};
const getSuggestedNewMealSchedule = ({ mealPlanEvents, externalEvents, modifiedMealTemplateIds }) => {
    const mealEvents = mealPlanEvents.flatMap(event => (isMealEvent(event) ? event : []));
    const activityEvents = mealPlanEvents.flatMap(event => (isActivityEvent(event) ? event : []));
    const mealModifications = getMealModificationsForEvents(externalEvents, mealEvents, activityEvents, modifiedMealTemplateIds);
    // get new meal events using modifications found
    const resolvedMealEvents = getResolvedMealEvents(mealEvents, mealModifications);
    return { meals: resolvedMealEvents, activities: activityEvents };
};
/**
 * Finds all modifications needed to existing meal times
 * given any conflicts with external events
 */
const getMealModificationsForEvents = (externalEvents, mealEvents, activityEvents, modifiedMealTemplateIds) => {
    // get all static, non-meal, events
    const staticEvents = getStaticEvents(externalEvents, activityEvents).sort((e1, e2) => compareAsc(e1.start, e2.start));
    // sort and pre-process meal events
    const mealEventCandidates = mealEvents
        .sort((e1, e2) => compareAsc(e1.start, e2.start))
        .map(me => {
        const isStatic = isStaticMealEvent(me, modifiedMealTemplateIds);
        return { ...me, isStatic };
    });
    // Match Notemeal meals to External meals
    const teamworksMealMatchModifications = getMealModificationsForExternalMealMatches(mealEventCandidates, staticEvents);
    // for each meal event, find if a meal needs to be moved
    const mealModifications = teamworksMealMatchModifications;
    for (const mealEventCandidate of mealEventCandidates) {
        // can't move static meals or meals that have been matched to an external meal
        if (mealEventCandidate.isStatic || findMealModificationForMeal(mealModifications, mealEventCandidate)) {
            continue;
        }
        const mealMod = getModificationForMeal(mealEventCandidate, mealEventCandidates, staticEvents, mealModifications);
        if (mealMod) {
            mealModifications.push(mealMod);
        }
    }
    return mealModifications;
};
/**
 * Returns a list of static events.
 * Static events are events that cannot be moved.
 */
const getStaticEvents = (externalEvents, activityEvents) => {
    const staticEvents = [];
    // all external events are static
    for (const event of externalEvents) {
        if (event.isAllDay) {
            // skip all day events since they shouldn't conflict with meal times
            continue;
        }
        staticEvents.push(event);
    }
    // all activities are static
    for (const activityEvent of activityEvents) {
        staticEvents.push(activityEvent);
    }
    return staticEvents;
};
const getModificationForMeal = (mealEvent, mealEvents, orderedStaticEvents, mealModifications) => {
    const timeOffset = getTimeOffsetForMeal(mealEvent, orderedStaticEvents, mealEvents, mealModifications);
    return timeOffset === 0 ? null : { mealEvent, timeOffset };
};
/**
 * Returns the new meal events using the list of meal modifications
 */
const getResolvedMealEvents = (mealEvents, mealModifications) => {
    return mealEvents.map(me => {
        const modForMeal = findMealModificationForMeal(mealModifications, me);
        const { mealType, ...rest } = me;
        // adjust time if offset exists, else event stays the same
        return modForMeal
            ? {
                ...rest,
                id: v4(),
                oldId: me.id,
                oldStart: me.start,
                type: "SuggestedMealTemplate",
                start: addMinutes(me.start, modForMeal.timeOffset),
                end: addMinutes(me.start, me.durationInMinutes + modForMeal.timeOffset),
            }
            : { ...me, mealType, type: "MealTemplate" };
    });
};
/**
 * Returns whether a meal is static or not.
 * A meal is static if it is in the past or just started or has been modified
 */
const isStaticMealEvent = (event, modifiedMealTemplateIds) => {
    return modifiedMealTemplateIds.includes(event.id) || event.start <= new Date();
};
//# sourceMappingURL=getSuggestedNewMealSchedule.js.map